import { dynamicsApi, integrationsApi } from '@/lib/api/client'
import {
  DTOOLS_SI_QUOTE_ACTIONS,
  dtoolsSiQuoteActionsSchema,
  type DtoolsSiQuoteActions,
} from '@/lib/api/dtools-si-contract'
import {
  buildDtoolsSiProjectDetailParams,
  DTOOLS_SI_IMPORT_POST_TIMEOUT_MS,
  DTOOLS_SI_IMPORT_RECOVERY_WINDOW_MS,
  DTOOLS_SI_QUOTE_ACTIONS_REFETCH_INTERVAL_MS,
  getDtoolsSiQuoteActionsRefetchInterval,
} from '@/lib/api/dtools-si-constants'
import {
  removeDtoolsSiImportAttempt,
  saveDtoolsSiImportAttempt,
  type PersistedDtoolsSiAttempt,
} from '@/lib/dtools-si-import-attempt'
import {
  DtoolsSiImportFailedError,
  recoverDtoolsSiImportAttempt,
} from '@/lib/dtools-si-import-recovery'
import { QUERY_KEYS, type SiSearchFilters } from '@/lib/api/query-keys'
import type { QuoteData } from '@/lib/api/quotes'
import { toast } from '@/lib/hooks/use-toast'
import { trackEvent, trackException } from '@/lib/telemetry'
import { QuoteStateCode, QuoteStatusCode } from '@/lib/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { z } from 'zod'

const BRANCH_PREFIX_RE = /^\s*(\d{4})(?:\s|$)/

export function getBranchNumber(branchName: string | null | undefined): number | null {
  if (!branchName) return null
  const match = BRANCH_PREFIX_RE.exec(branchName)
  return match ? Number(match[1]) : null
}

export {
  DTOOLS_SI_QUOTE_ACTIONS,
  dtoolsSiChangeOrderImportSchema,
  dtoolsSiLinkedProjectSchema,
  dtoolsSiQuoteActionsSchema,
  dtoolsSiReplacementContextSchema,
} from '@/lib/api/dtools-si-contract'
export type {
  DtoolsSiChangeOrderImport,
  DtoolsSiLinkedProject,
  DtoolsSiQuoteAction,
  DtoolsSiQuoteActions,
  DtoolsSiReplacementContext,
} from '@/lib/api/dtools-si-contract'
export { DTOOLS_SI_QUOTE_ACTIONS_REFETCH_INTERVAL_MS }

export function useDtoolsSiQuoteActionsQuery({
  quoteId,
  enabled = true,
}: {
  quoteId: string | null | undefined
  enabled?: boolean
}) {
  const normalizedQuoteId = quoteId ?? null
  return useQuery<DtoolsSiQuoteActions>({
    queryKey: QUERY_KEYS.dtoolsSiQuoteActions(normalizedQuoteId),
    enabled: enabled && normalizedQuoteId !== null,
    staleTime: 0,
    refetchInterval: (query) => getDtoolsSiQuoteActionsRefetchInterval(query.state.data),
    queryFn: async ({ signal }) => {
      const res = await integrationsApi.get(`/dtools-si/quotes/${normalizedQuoteId}/actions`, {
        signal,
        params: { supported_actions: DTOOLS_SI_QUOTE_ACTIONS.join(',') },
      })
      return dtoolsSiQuoteActionsSchema.parse(res.data)
    },
  })
}

const siCONumberSchema = z.union([z.number(), z.string()]).nullable()
export const QUOTE_DTOOLS_STATE_PENDING = 309890001
const QUOTE_DTOOLS_STATE_IMPORTED = 309890002
export const QUOTE_DTOOLS_STATE_FAILED = 309890003

export const siProjectListRowSchema = z
  .object({
    Id: z.string(),
    Name: z.string().nullable(),
    Number: z.string().nullable(),
    Client: z.string().nullable(),
    ClientNumber: z.string().nullable(),
    CONumber: siCONumberSchema,
    Approved: z.boolean().nullable(),
    Archived: z.boolean().nullable(),
    Deleted: z.boolean().nullable(),
    ImportedOn: z.string().nullable(),
    PublishedOn: z.string().nullable(),
    Price: z.number().nullable(),
    Progress: z.string().nullable(),
  })
  .passthrough()

export type SiProjectListRow = z.infer<typeof siProjectListRowSchema>

export const siProjectListResponseSchema = z
  .object({
    Projects: z.array(siProjectListRowSchema),
    TotalCount: z.number(),
  })
  .passthrough()

export type SiProjectListResponse = z.infer<typeof siProjectListResponseSchema>

export const siPaymentTermSchema = z
  .object({
    Payment: z.string(),
    BillingAmount: z.number().nullable(),
    BillingPercentage: z.number().nullable(),
    UseFixedAmount: z.boolean(),
    CONumber: siCONumberSchema,
    BillingDate: z.string().nullable().optional(),
  })
  .passthrough()

export type SiPaymentTerm = z.infer<typeof siPaymentTermSchema>

export const siProjectDetailItemSchema = z
  .object({
    Id: z.string(),
    PackageId: z.union([z.number(), z.string()]).nullable(),
    ParentId: z.string().nullable().optional(),
  })
  .passthrough()

export type SiProjectDetailItem = z.infer<typeof siProjectDetailItemSchema>

export const siProjectDetailSchema = z
  .object({
    Id: z.string(),
    Number: z.string().nullable(),
    Name: z.string().nullable(),
    Client: z.string().nullable(),
    Items: z.array(siProjectDetailItemSchema),
    PaymentTerms: z.array(siPaymentTermSchema).nullable(),
    Price: z.number().nullable(),
    PriceWithoutTax: z.number().nullable(),
    Cost: z.number().nullable(),
    Revision: z.number().nullable().optional(),
    PublishedOn: z.string().nullable().optional(),
  })
  .passthrough()

export type SiProjectDetail = z.infer<typeof siProjectDetailSchema>

export const siProjectCompositeMetaSchema = z.object({
  base_is_approved: z.boolean(),
  approved_co_numbers: z.array(z.number()),
  unapproved_co_numbers: z.array(z.number()),
  latest_approved_co_number: z.number().nullable(),
  base_price: z.number(),
  approved_co_prices: z.record(z.string(), z.number()),
  total_price: z.number(),
  base_revision: z.number().nullable().optional(),
  base_published_on: z.string().nullable().optional(),
  target_co_number: z.number().nullable().optional(),
  target_co_price: z.number().nullable().optional(),
  target_co_published_on: z.string().nullable().optional(),
})

export type SiProjectCompositeMeta = z.infer<typeof siProjectCompositeMetaSchema>

export const siProjectCompositeResponseSchema = z.object({
  project: siProjectDetailSchema,
  composite: siProjectCompositeMetaSchema,
})

export type SiProjectCompositeResponse = z.infer<typeof siProjectCompositeResponseSchema>

export const dtoolsSiAckFailureSchema = z.object({
  co_number: z.number().nullable(),
  error: z.string(),
})

// Pre-tax reconciliation against SI (see `formatDtoolsSiFinancialSummary`).
// Optional: an older API build omits it, and the API omits it when SI carries
// no pre-tax figure.
const dtoolsSiFinancialSummarySchema = z
  .object({
    si_pretax_price: z.number(),
    cpq_pretax_price: z.number(),
    pretax_difference: z.number(),
    supplies_pretax_delta: z.number(),
    unabsorbed_price: z.number(),
    header_vs_rows: z.number(),
    rounding_absorbed_into_supplies: z.boolean(),
    si_labor_tax_not_imported: z.number().optional(),
  })
  .nullable()
  .optional()

const dtoolsSiCompletedImportResponseSchema = z.object({
  status: z.string(),
  quote_id: z.string(),
  sync_log_id: z.string(),
  si_project_id: z.string(),
  financial_summary: dtoolsSiFinancialSummarySchema,
  lines_created: z.number(),
  milestones_created: z.number(),
  skipped_non_line_items: z.number(),
  approved_co_numbers: z.array(z.number()),
  unapproved_co_numbers: z.array(z.number()),
  latest_approved_co_number: z.number().nullable(),
  si_co_number: z.number().nullable().optional(),
  total_price: z.number(),
  ack_failures: z.array(dtoolsSiAckFailureSchema),
  activation_warning: z.string().nullable().optional(),
  change_order_quote_id: z.string().optional(),
  replaced_existing: z.boolean().optional().default(false),
  previous_co_number: z.number().nullable().optional(),
  retained_lines: z.number().optional(),
  removed_lines: z.number().optional(),
  replacement_operation_count: z.number().optional(),
  completion_recovered: z.literal(false).optional().default(false),
})

const dtoolsSiRecoveredImportResponseSchema = z.object({
  status: z.enum(['imported', 'imported_ack_pending']),
  quote_id: z.string(),
  sync_log_id: z.string(),
  si_project_id: z.string(),
  replaced_existing: z.boolean(),
  completion_recovered: z.literal(true),
})

export const dtoolsSiImportResponseSchema = dtoolsSiCompletedImportResponseSchema
export type DtoolsSiImportResponse = z.infer<typeof dtoolsSiImportResponseSchema>

const dtoolsSiProjectImportResponseSchema = z.union([
  dtoolsSiCompletedImportResponseSchema,
  dtoolsSiRecoveredImportResponseSchema,
])
type DtoolsSiProjectImportResponse = z.infer<typeof dtoolsSiProjectImportResponseSchema>

function isRecoverableDtoolsSiImportError(error: unknown) {
  if (!isAxiosError(error)) return false
  return !error.response || error.code === 'ECONNABORTED' || [429, 500, 502, 503, 504].includes(error.response.status)
}

export const dtoolsSiExportParitySchema = z.object({
  header: z.object({
    subtotal: z.number(),
    tax: z.number(),
    total: z.number(),
    cost: z.number(),
    hours: z.number(),
  }),
  export: z.object({
    subtotal: z.number(),
    tax: z.number(),
    total: z.number(),
    cost: z.number(),
    hours: z.number(),
  }),
  diff: z.object({
    subtotal: z.number(),
    tax: z.number(),
    total: z.number(),
    cost: z.number(),
    hours: z.number(),
  }),
})

export const dtoolsSiExportDryRunSchema = z.object({
  status: z.literal('dry_run'),
  quote_id: z.string(),
  branch_number: z.number(),
  item_count: z.number(),
  parity: dtoolsSiExportParitySchema,
})

export const dtoolsSiExportResponseSchema = z.object({
  status: z.enum(['accepted', 'failed']),
  quote_id: z.string().optional(),
  sync_log_id: z.string(),
  message_id: z.string().nullable().optional(),
  si_project_id: z.string().optional(),
  error: z.string().optional(),
  tracking_warning: z.string().nullable().optional(),
  parity: dtoolsSiExportParitySchema.optional(),
})

export type DtoolsSiExportDryRun = z.infer<typeof dtoolsSiExportDryRunSchema>
export type DtoolsSiExportResponse = z.infer<typeof dtoolsSiExportResponseSchema>

export const dtoolsSiUnmappedProductSchema = z.object({
  si_item_id: z.string(),
  si_product_key: z.string().nullable().optional(),
  si_model: z.string().nullable(),
  si_part_number: z.string().nullable(),
  si_manufacturer: z.string().nullable(),
  // Optional new fields — older BE deploys won't include them. Defaults keep
  // the UI in current-behavior mode (search-only, no Create option) until the
  // BE upgrade lands.
  si_catalog_id: z.string().nullable().default(null),
  si_is_bluedog: z.boolean().default(false),
  si_requires_labor_product: z.boolean().default(false),
  // A change-order removal: only the existing CPQ line's product can match,
  // so Create is never offered.
  si_is_removal: z.boolean().default(false),
  mapped_product_issue: z.string().optional(),
})

export type DtoolsSiUnmappedProduct = z.infer<typeof dtoolsSiUnmappedProductSchema>

export const dtoolsSiUnmappedRoomSchema = z.object({
  si_location_id: z.string().nullable(),
  // Bare location name — stays bare in v2; the path arrives separately.
  si_location_name: z.string().nullable(),
  // Nested-import (v2) fields. A legacy import emits NEITHER, so their presence
  // is the mode discriminator (see `isNestedImportRoom`). `si_location_path` is
  // the backslash-separated SI path ("Cabana\\Bar"; equal to si_location_name at
  // top level) and is the `room_overrides` key, verbatim.
  si_parent_location_name: z.string().nullable().optional(),
  si_location_path: z.string().nullable().optional(),
  count: z.number().default(1),
  // Suggested existing dsy_accountroomid (LLM-derived). Null = LLM said
  // "create a new room" OR LLM didn't run / failed. Optional for
  // backward-compat with older BEs that don't stamp suggestions yet.
  suggested_room_id: z.string().nullable().optional(),
})

export type DtoolsSiUnmappedRoom = z.infer<typeof dtoolsSiUnmappedRoomSchema>

/**
 * `room_overrides` request body: SI room key -> `room_create_value` or an
 * existing `dsy_accountroomid`. Build the keys with `getSiRoomOverrideKey`
 * (src/lib/dtools-si-room-mappings.ts) — they are read off the 422 rows, never
 * composed here.
 */
export type DtoolsSiRoomOverrides = Record<string, string>

export const dtoolsSiUnmappedLaborTypeSchema = z.object({
  labor_type_name: z.string(),
  count: z.number().default(1),
  total_hours: z.number().default(0),
  suggested_cpq_field: z.string().nullable().optional(),
})

export type DtoolsSiUnmappedLaborType = z.infer<typeof dtoolsSiUnmappedLaborTypeSchema>

export const dtoolsSiUnmappedSystemSchema = z.object({
  si_system_id: z.string().nullable().optional(),
  // The D-Tools project system name — the `system_overrides` key, verbatim.
  si_system_name: z.string(),
  count: z.number().default(1),
  // Low-confidence LLM pick (dsy_system option value). Null = no suggestion.
  suggested_system_value: z.number().nullable().optional(),
})

export type DtoolsSiUnmappedSystem = z.infer<typeof dtoolsSiUnmappedSystemSchema>

/** `system_overrides` request body: SI system name -> dsy_system option value. */
export type DtoolsSiSystemOverrides = Record<string, number>

export const dtoolsSiUnmappedTaxCodeSchema = z.object({
  si_zip_code: z.string().nullable(),
  si_tax_rate: z.number().nullable(),
  si_tax_amount: z.number(),
  tax_code_id: z.string().nullable(),
  tax_code_name: z.string().nullable(),
  reason: z.string(),
})

export type DtoolsSiUnmappedTaxCode = z.infer<typeof dtoolsSiUnmappedTaxCodeSchema>

export const dtoolsSiImportExceptionsSchema = z.object({
  unmapped_products: z.array(dtoolsSiUnmappedProductSchema).default([]),
  unmapped_rooms: z.array(dtoolsSiUnmappedRoomSchema).default([]),
  unmapped_systems: z.array(dtoolsSiUnmappedSystemSchema).default([]),
  unmapped_labor_types: z.array(dtoolsSiUnmappedLaborTypeSchema).default([]),
  unresolved_parents: z.array(z.record(z.unknown())).default([]),
  unmapped_tax_codes: z.array(dtoolsSiUnmappedTaxCodeSchema).default([]),
})

export type DtoolsSiImportExceptions = z.infer<typeof dtoolsSiImportExceptionsSchema>

export const dtoolsSiImportResolutionLaborFieldSchema = z.object({
  field: z.string().min(1),
  label: z.string().min(1),
})

export type DtoolsSiImportResolutionLaborField = z.infer<typeof dtoolsSiImportResolutionLaborFieldSchema>

export const dtoolsSiImportResolutionSystemSchema = z.object({
  value: z.number(),
  label: z.string().min(1),
})

export type DtoolsSiImportResolutionSystem = z.infer<typeof dtoolsSiImportResolutionSystemSchema>

export const dtoolsSiImportResolutionSchema = z.object({
  room_create_value: z.string().min(1),
  labor_fields: z.array(dtoolsSiImportResolutionLaborFieldSchema).min(1),
  // Closed set for `system_overrides`. Optional for older BEs; an empty list
  // means systems cannot be resolved in-dialog.
  systems: z.array(dtoolsSiImportResolutionSystemSchema).default([]),
})

export type DtoolsSiImportResolution = z.infer<typeof dtoolsSiImportResolutionSchema>

export const dtoolsSiImportErrorBodySchema = z.object({
  message: z.string(),
  exceptions: dtoolsSiImportExceptionsSchema.optional().default({}),
  resolution: dtoolsSiImportResolutionSchema.optional(),
  change_order_quote_id: z.string().optional(),
  si_co_number: z.number().optional(),
})

export type DtoolsSiImportErrorBody = z.infer<typeof dtoolsSiImportErrorBodySchema>

export class DtoolsSiImportValidationError extends Error {
  exceptions: DtoolsSiImportExceptions
  resolution: DtoolsSiImportResolution | null
  changeOrderQuoteId: string | null
  siCoNumber: number | null
  constructor(
    message: string,
    exceptions: DtoolsSiImportExceptions,
    resolution: DtoolsSiImportResolution | null,
    changeOrderQuoteId: string | null = null,
    siCoNumber: number | null = null
  ) {
    super(message)
    this.name = 'DtoolsSiImportValidationError'
    this.exceptions = exceptions
    this.resolution = resolution
    this.changeOrderQuoteId = changeOrderQuoteId
    this.siCoNumber = siCoNumber
  }
}

export class DtoolsSiChangeOrderImportError extends Error {
  changeOrderQuoteId: string | null
  siCoNumber: number | null
  constructor(message: string, changeOrderQuoteId: string | null = null, siCoNumber: number | null = null) {
    super(message)
    this.name = 'DtoolsSiChangeOrderImportError'
    this.changeOrderQuoteId = changeOrderQuoteId
    this.siCoNumber = siCoNumber
  }
}

function parseDtoolsSiImportErrorBody(error: unknown) {
  if (!isAxiosError(error)) return null
  const data = error.response?.data as { detail?: unknown } | undefined
  const candidate = data?.detail ?? data
  return dtoolsSiImportErrorBodySchema.safeParse(candidate)
}

type DtoolsSiSearchVariables = {
  branchNumber: number | null | undefined
  searchText?: string
  includeImported?: boolean
  includeArchived?: boolean
  pageNumber?: number
  pageSize?: number
  enabled?: boolean
}

export function useDtoolsSiSearchQuery({
  branchNumber,
  searchText,
  includeImported = false,
  includeArchived = false,
  pageNumber = 1,
  pageSize = 50,
  enabled = true,
}: DtoolsSiSearchVariables) {
  const filters: SiSearchFilters = {
    branchNumber: branchNumber ?? null,
    searchText: searchText?.trim() || null,
    includeImported,
    includeArchived,
    pageNumber,
    pageSize,
  }
  return useQuery({
    queryKey: QUERY_KEYS.dtoolsSiSearch(filters),
    enabled: enabled && branchNumber != null,
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      const res = await integrationsApi.post(
        '/dtools-si/projects/search',
        {
          branch_number: branchNumber,
          search_text: filters.searchText,
          include_imported: includeImported,
          include_archived: includeArchived,
          page_number: pageNumber,
          page_size: pageSize,
        },
        { signal }
      )
      return siProjectListResponseSchema.parse(res.data)
    },
  })
}

type DtoolsSiProjectDetailVariables = {
  branchNumber: number | null | undefined
  projectId: string | null | undefined
  projectNumber: string | null | undefined
  throughCoNumber?: number | null
  replaceExisting?: boolean
  enabled?: boolean
}

export function useDtoolsSiProjectDetailQuery({
  branchNumber,
  projectId,
  projectNumber,
  throughCoNumber = null,
  replaceExisting = false,
  enabled = true,
}: DtoolsSiProjectDetailVariables) {
  const normalizedProjectNumber = projectNumber?.trim() || null
  const isEnabled = enabled && branchNumber != null && !!projectId && !!normalizedProjectNumber
  return useQuery({
    queryKey: QUERY_KEYS.dtoolsSiProjectDetail(
      branchNumber ?? null,
      projectId ?? null,
      normalizedProjectNumber,
      throughCoNumber,
      replaceExisting
    ),
    enabled: isEnabled,
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      const params = buildDtoolsSiProjectDetailParams({
        projectNumber: normalizedProjectNumber!,
        throughCoNumber,
        replaceExisting,
      })
      const search = `?${params.toString()}`
      const res = await integrationsApi.get(`/dtools-si/projects/${branchNumber}/${projectId}${search}`, { signal })
      return siProjectCompositeResponseSchema.parse(res.data)
    },
  })
}

export type ImportDtoolsSiProjectVariables = {
  branchNumber: number
  siProjectId: string
  siProjectNumber: string | null
  siCoNumber?: number | null
  replaceExisting?: boolean
  targetQuoteId: string
  productOverrides?: Record<string, string>
  /** Composite SI item Ids ('1','2',…) the user opted to Create-Product for. */
  productCreateSiItemIds?: string[]
  /**
   * SI room key -> backend-provided create value OR existing dsy_accountroomid
   * UUID. The key is whatever `getSiRoomOverrideKey` read off the 422 row:
   * `si_location_path` verbatim on a v2 import, `si_location_name` on a legacy
   * one. Never synthesise a key — a v2 import rejects (HTTP 400) any key that
   * is not one of the project's SI location paths.
   */
  roomOverrides?: DtoolsSiRoomOverrides
  /** SI labor type name -> backend-provided CPQ labor field. */
  laborTypeOverrides?: Record<string, string>
  /** D-Tools system name -> dsy_system option value (from `resolution.systems`). */
  systemOverrides?: DtoolsSiSystemOverrides
  /** Selected CPQ tax code ID. Server validates active ZIP coverage; CPQ rate drives tax math. */
  taxCodeId?: string
}

const dtoolsSiTaxCodeSchema = z.object({
  msdyn_taxcodeid: z.string(),
  msdyn_name: z.string(),
  msdyn_taxrate: z.number(),
  dsy_zipcodes: z.string().nullable().optional(),
})

export type DtoolsSiTaxCode = z.infer<typeof dtoolsSiTaxCodeSchema>

function encodeODataLiteral(value: string) {
  return encodeURIComponent(value.replace(/'/g, "''"))
}

export function useDtoolsSiTaxCodeSearchQuery(search: string, enabled = true) {
  const normalized = search.trim()
  return useQuery({
    queryKey: QUERY_KEYS.taxCodes(normalized),
    queryFn: async ({ signal }) => {
      const encoded = encodeODataLiteral(normalized)
      const res = await dynamicsApi.get<{ value: unknown[] }>(
        `/msdyn_taxcodes?$select=msdyn_taxcodeid,msdyn_name,msdyn_taxrate,dsy_zipcodes&$filter=statecode eq 0 and (contains(dsy_zipcodes,'${encoded}') or contains(msdyn_name,'${encoded}'))&$top=50`,
        { signal }
      )
      return z.array(dtoolsSiTaxCodeSchema).parse(res.data.value).sort(
        (a, b) => (a.dsy_zipcodes?.length ?? 0) - (b.dsy_zipcodes?.length ?? 0)
      )
    },
    enabled: enabled && normalized.length >= 2,
    staleTime: 30_000,
  })
}

export function isDtoolsSiQuoteLocked(quote: QuoteData | null | undefined): boolean {
  if (!quote?.dsy_dtoolssiprojectid) return false
  return quote.dsy_dtoolssilaststate === QUOTE_DTOOLS_STATE_PENDING
}


export function isQuoteDraftEditable(quote: QuoteData | null | undefined): boolean {
  return quote?.statecode === QuoteStateCode.Draft &&
    quote.statuscode !== QuoteStatusCode.Draft_Rebasing && !isDtoolsSiQuoteLocked(quote)
}

export function useDtoolsSiExportDryRunQuery({
  quoteId,
  enabled = true,
}: {
  quoteId: string | null | undefined
  enabled?: boolean
}) {
  return useQuery({
    queryKey: QUERY_KEYS.dtoolsSiExportDryRun(quoteId ?? null),
    enabled: enabled && !!quoteId,
    staleTime: 0,
    gcTime: 0,
    queryFn: async ({ signal }) => {
      const res = await integrationsApi.post(`/quote/${quoteId}/export-to-dtools-si/dry-run`, {}, { signal })
      return dtoolsSiExportDryRunSchema.parse(res.data)
    },
  })
}

export function useExportQuoteToDtoolsSi() {
  const queryClient = useQueryClient()

  return useMutation<DtoolsSiExportResponse, Error, { quoteId: string }>({
    mutationKey: ['dtools-si', 'export-quote'],
    mutationFn: async ({ quoteId }) => {
      const res = await integrationsApi.post(`/quote/${quoteId}/export-to-dtools-si`)
      return dtoolsSiExportResponseSchema.parse(res.data)
    },
    onSuccess: (data) => {
      trackEvent('dtools_si_export_started', {
        quote_id: data.quote_id ?? '',
        si_project_id: data.si_project_id ?? '',
      })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quote() })
      if (data.quote_id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiQuoteActions(data.quote_id) })
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiExportRoot })
    },
    onError: (error) => {
      trackException(error, { tag: 'dtools_si_export_failed' })
      toast({
        title: 'D-Tools SI export failed',
        error,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Send a blank "shell" project to D-Tools SI from a Draft quote. The quote
 * lands Pending — still import-eligible — so the rep
 * builds it out in D-Tools, then pulls it back via the import dialog.
 */
export function useSendShellDtoolsSiProject() {
  const queryClient = useQueryClient()

  return useMutation<DtoolsSiExportResponse, Error, { quoteId: string }>({
    mutationKey: ['dtools-si', 'export-shell'],
    mutationFn: async ({ quoteId }) => {
      const res = await integrationsApi.post(`/quote/${quoteId}/export-shell-to-dtools-si`)
      return dtoolsSiExportResponseSchema.parse(res.data)
    },
    onSuccess: (data) => {
      trackEvent('dtools_si_shell_export_started', {
        quote_id: data.quote_id ?? '',
        si_project_id: data.si_project_id ?? '',
      })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quote() })
      if (data.quote_id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiQuoteActions(data.quote_id) })
      }
    },
    onError: (error) => {
      trackException(error, { tag: 'dtools_si_shell_export_failed' })
      toast({
        title: 'Send to D-Tools SI failed',
        error,
        variant: 'destructive',
      })
    },
  })
}

export function useImportDtoolsSiProject() {
  const queryClient = useQueryClient()

  return useMutation<DtoolsSiProjectImportResponse, Error, ImportDtoolsSiProjectVariables>({
    mutationKey: ['dtools-si', 'import-project'],
    mutationFn: async ({
      branchNumber,
      siProjectId,
      siProjectNumber,
      siCoNumber,
      replaceExisting,
      targetQuoteId,
      productOverrides,
      productCreateSiItemIds,
      roomOverrides,
      laborTypeOverrides,
      systemOverrides,
      taxCodeId,
    }) => {
      const startedAt = Date.now()
      const importAttemptId = crypto.randomUUID()
      const attempt: PersistedDtoolsSiAttempt = {
        version: 1,
        quoteId: targetQuoteId,
        siProjectId,
        attemptId: importAttemptId,
        replaceExisting: replaceExisting ?? false,
        startedAt,
        recoveryDeadlineAt: startedAt + DTOOLS_SI_IMPORT_POST_TIMEOUT_MS + DTOOLS_SI_IMPORT_RECOVERY_WINDOW_MS,
      }
      saveDtoolsSiImportAttempt(attempt)
      try {
        const body: Record<string, unknown> = {
          branch_number: branchNumber,
          si_project_id: siProjectId,
          target_quote_id: targetQuoteId,
          import_attempt_id: importAttemptId,
        }
        if (siProjectNumber) body.si_project_number = siProjectNumber
        if (siCoNumber != null) body.si_co_number = siCoNumber
        if (replaceExisting) body.replace_existing = true
        if (productOverrides && Object.keys(productOverrides).length > 0) {
          body.product_overrides = productOverrides
        }
        if (productCreateSiItemIds && productCreateSiItemIds.length > 0) {
          body.product_create_si_item_ids = productCreateSiItemIds
        }
        if (roomOverrides && Object.keys(roomOverrides).length > 0) {
          body.room_overrides = roomOverrides
        }
        if (laborTypeOverrides && Object.keys(laborTypeOverrides).length > 0) {
          body.labor_type_overrides = laborTypeOverrides
        }
        if (systemOverrides && Object.keys(systemOverrides).length > 0) {
          body.system_overrides = systemOverrides
        }
        if (taxCodeId) body.tax_code_id = taxCodeId
        const res = await integrationsApi.post('/dtools-si/import-project', body, {
          timeout: DTOOLS_SI_IMPORT_POST_TIMEOUT_MS,
        })
        const result = dtoolsSiProjectImportResponseSchema.parse(res.data)
        removeDtoolsSiImportAttempt(targetQuoteId, siProjectId, importAttemptId)
        return result
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          removeDtoolsSiImportAttempt(targetQuoteId, siProjectId, importAttemptId)
          const parsed = parseDtoolsSiImportErrorBody(error)
          if (parsed?.success) {
            throw new DtoolsSiImportValidationError(
              parsed.data.message,
              parsed.data.exceptions,
              parsed.data.resolution ?? null,
              parsed.data.change_order_quote_id ?? null,
              parsed.data.si_co_number ?? null
            )
          }
        }
        if (isRecoverableDtoolsSiImportError(error)) {
          try {
            const result = await recoverDtoolsSiImportAttempt(attempt, {
              requestStatus: async (pendingAttempt, timeoutMs) => {
                const res = await integrationsApi.get('/dtools-si/import-status', {
                  params: {
                    quote_id: pendingAttempt.quoteId,
                    si_project_id: pendingAttempt.siProjectId,
                    import_attempt_id: pendingAttempt.attemptId,
                  },
                  timeout: timeoutMs,
                })
                return res.data
              },
            })
            removeDtoolsSiImportAttempt(targetQuoteId, siProjectId, importAttemptId)
            return result
          } catch (recoveryError) {
            if (recoveryError instanceof DtoolsSiImportFailedError) {
              removeDtoolsSiImportAttempt(targetQuoteId, siProjectId, importAttemptId)
            }
            throw recoveryError
          }
        }
        if (isAxiosError(error) && error.response && error.response.status < 500) {
          removeDtoolsSiImportAttempt(targetQuoteId, siProjectId, importAttemptId)
        }
        throw error
      }
    },
    onSuccess: (data, variables) => {
      trackEvent('dtools_si_import_succeeded', {
        si_project_id: variables.siProjectId,
        quote_id: data.quote_id,
        replace_existing: variables.replaceExisting ? 'true' : 'false',
      })
      queryClient.setQueryData<QuoteData>(QUERY_KEYS.quote(), (current) =>
        current
          ? {
              ...current,
              dsy_dtoolssiprojectid: data.si_project_id,
              dsy_dtoolssilaststate: QUOTE_DTOOLS_STATE_IMPORTED,
            }
          : current
      )
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quote() })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiQuoteActions(data.quote_id) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailsRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailSummaryRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiSearchRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accountRooms })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones })
    },
    onError: (error, variables) => {
      if (error instanceof DtoolsSiImportValidationError) return
      trackException(error, {
        tag: variables.replaceExisting ? 'dtools_si_replace_failed' : 'dtools_si_import_failed',
        si_project_id: variables.siProjectId,
      })
      toast({
        title: variables.replaceExisting ? 'D-Tools SI replacement failed' : 'D-Tools SI import failed',
        error,
        variant: 'destructive',
      })
    },
  })
}

export function useCreateDtoolsSiChangeOrder() {
  const queryClient = useQueryClient()

  return useMutation<DtoolsSiImportResponse, Error, { quoteId: string; branchNumber: number; taxCodeId: string }>({
    mutationKey: ['dtools-si', 'create-change-order'],
    mutationFn: async ({ quoteId, branchNumber, taxCodeId }) => {
      try {
        const res = await integrationsApi.post(`/quote/${quoteId}/dtools-si/create-change-order`, {
          branch_number: branchNumber,
          tax_code_id: taxCodeId,
        })
        return dtoolsSiImportResponseSchema.parse(res.data)
      } catch (error) {
        const parsed = parseDtoolsSiImportErrorBody(error)
        if (parsed?.success) {
          if (isAxiosError(error) && error.response?.status === 422) {
            throw new DtoolsSiImportValidationError(
              parsed.data.message,
              parsed.data.exceptions,
              parsed.data.resolution ?? null,
              parsed.data.change_order_quote_id ?? null,
              parsed.data.si_co_number ?? null
            )
          }
          if (parsed.data.change_order_quote_id) {
            throw new DtoolsSiChangeOrderImportError(
              parsed.data.message,
              parsed.data.change_order_quote_id ?? null,
              parsed.data.si_co_number ?? null
            )
          }
        }
        throw error
      }
    },
    onSuccess: (data, variables) => {
      trackEvent('dtools_si_change_order_import_succeeded', {
        quote_id: variables.quoteId,
        change_order_quote_id: data.change_order_quote_id ?? '',
        si_co_number: data.si_co_number != null ? String(data.si_co_number) : '',
      })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quote() })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dtoolsSiQuoteActions(variables.quoteId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailsRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailSummaryRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones })
    },
    onError: (error, variables) => {
      if (error instanceof DtoolsSiImportValidationError || error instanceof DtoolsSiChangeOrderImportError) return
      trackException(error, {
        tag: 'dtools_si_change_order_import_failed',
        quote_id: variables.quoteId,
      })
    },
  })
}
